import React, { ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { noop } from 'lodash';
import { Center, Container, Stack } from '@fiverr-private/layout_components';
import { Loader } from '@fiverr-private/feedback';
import { ModalFooter, ModalHeader } from '@fiverr-private/overlay';
import { Button } from '@fiverr-private/button';
import { ArrowLeftIcon } from '@fiverr-private/visuals';
import { useBreakpoint } from '@fiverr-private/hooks';
import { UpgradeToAdvancedStepContent } from '../components/UpgradeToAdvancedStep/UpgradeToAdvancedStepContent';
import { RequestUpgradeModalContent } from '../entries/RequestUpgradeModal';
import { AppI18n } from '../utils/i18n';
import { UpgradeToAdvancedPlanButton } from '../components/UpgradeToAdvancedPlanButton/UpgradeToAdvancedPlanButton';
import { useUpgradeStepData, UseUpgradeStepDataOptions } from './useUpgradeStepData';

export interface UseUpgradeStepOptions {
    biEvents?: {
        pageName: string;
        upgradeSource?: string;
        sellerId?: number;
    };
    onRequestUpgrade?: () => void;
    onBack?: () => void;
    redirectUrl?: string;
    title?: ReactNode;
    isUsSeller?: boolean;
}

export const useUpgradeStep = (props: UseUpgradeStepOptions) => {
    const [queryClient] = React.useState(() => new QueryClient());

    return (
        <QueryClientProvider client={queryClient}>
            <UpgradeStepContent {...props} />
        </QueryClientProvider>
    );
};

const MOBILE_RESOLUTIONS = ['default', 'sm'];

const UpgradeStepContent = ({
    initialData,
    onError,
    onRequestUpgrade = noop,
    biEvents,
    onBack,
    redirectUrl,
    title,
    isUsSeller,
}: UseUpgradeStepOptions & UseUpgradeStepDataOptions) => {
    const { isFetching, data, isAdmin, organizationAdmin } = useUpgradeStepData({ initialData, onError });

    const breakpoint = useBreakpoint();

    if (isFetching || !data) {
        return (
            <Center width="100%" height="100%">
                <Loader />
            </Center>
        );
    }

    if (isAdmin) {
        return (
            <>
                {title && <ModalHeader>{title}</ModalHeader>}
                <UpgradeToAdvancedStepContent
                    compliancePackageData={data.compliancePackageData}
                    currentPlan={data.subscriptionData}
                    isUsSeller={isUsSeller}
                />
                <ModalFooter>
                    <Stack justifyContent="spaceBetween" width="100%">
                        <Container display={{ default: 'none', md: 'block' }}>
                            {onBack && (
                                <Button onClick={onBack} variant="ghost">
                                    <Stack alignItems="center">
                                        <ArrowLeftIcon />
                                        <AppI18n k="upgrade_step.back" />
                                    </Stack>
                                </Button>
                            )}
                        </Container>
                        <UpgradeToAdvancedPlanButton
                            fullWidth={breakpoint && MOBILE_RESOLUTIONS.includes(breakpoint)}
                            redirectUrl={redirectUrl}
                            biData={biEvents}
                        >
                            <AppI18n k="upgrade_step.upgrade_to_advanced" />
                        </UpgradeToAdvancedPlanButton>
                    </Stack>
                </ModalFooter>
            </>
        );
    }

    return (
        <RequestUpgradeModalContent
            initialAdminMemberData={organizationAdmin}
            requestType="upgrade"
            currentPlan="pro_essential"
            pageName={biEvents?.pageName || ''}
            upgradeSource={biEvents?.upgradeSource}
            sellerId={biEvents?.sellerId}
            onSuccess={onRequestUpgrade}
        />
    );
};
